import React, { createContext, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { getUserPermissions, isAdmin } from '../../utils/permissionCache';
import jwt_decode from 'jwt-decode';

/**
 * Context so child pages (e.g. ListViewLayout) can show one loader for both
 * permission check and data load instead of two separate loaders.
 */
export const PermissionCheckingContext = createContext({ permissionChecking: false });

/**
 * Permission-Protected Route Component
 *
 * Checks if user has permission to access an object before rendering the route.
 * While checking permission, still renders children so they can show a single
 * "Loading..." state; use PermissionCheckingContext in children to avoid double loaders.
 *
 * Usage:
 * <PermissionProtectedRoute action="read">
 *   <ListView />
 * </PermissionProtectedRoute>
 */

const PermissionProtectedRoute = ({ children, action = 'read', objectNameParam = 'objectName' }) => {
  const params = useParams();
  const objectName = params[objectNameParam];

  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkPermission();
  }, [objectName, action]);

  const checkPermission = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 [Permission Guard] === PERMISSION CHECK STARTED ===');
      console.log(`🔍 [Permission Guard] Object: ${objectName}, Action: ${action}`);

      // Get user info from session or decode from token
      let userInfoStr = sessionStorage.getItem('userinfo');
      console.log('🔍 [Permission Guard] Raw userinfo from session:', userInfoStr);

      // If userinfo not in sessionStorage, decode from token in localStorage
      if (!userInfoStr) {
        console.log('🔍 [Permission Guard] No userinfo in sessionStorage, checking token...');
        const token = localStorage.getItem('token');

        if (!token) {
          console.error('❌ [Permission Guard] No token in localStorage');
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        try {
          const decodedToken = jwt_decode(token);
          console.log('🔍 [Permission Guard] Decoded token:', decodedToken);

          // Store in sessionStorage for future use
          sessionStorage.setItem('userinfo', JSON.stringify(decodedToken));
          userInfoStr = JSON.stringify(decodedToken);
          console.log('✅ [Permission Guard] Reconstructed userinfo from token');
        } catch (error) {
          console.error('❌ [Permission Guard] Error decoding token:', error);
          setError('Invalid authentication token');
          setLoading(false);
          return;
        }
      }

      const userInfo = JSON.parse(userInfoStr);
      console.log('🔍 [Permission Guard] Parsed userInfo:', userInfo);
      console.log('🔍 [Permission Guard] Role details:', {
        role_id: userInfo.role_id,
        role_name: userInfo.role_name,
        userrole: userInfo.userrole,
        id: userInfo.id,
        email: userInfo.email
      });

      // 🔓 ADMIN BYPASS: Admins have full access
      const adminStatus = isAdmin(userInfo);
      console.log(`🔍 [Permission Guard] Admin check result: ${adminStatus}`);

      if (adminStatus) {
        console.log(`🔓 [Permission Guard] ADMIN access - allowing ${action} on ${objectName}`);
        setHasPermission(true);
        setLoading(false);
        return;
      }

      const roleId = userInfo.role_id;
      console.log(`🔍 [Permission Guard] User role_id: ${roleId}`);

      if (!roleId) {
        console.error('❌ [Permission Guard] User has no role_id in userinfo');
        setError('User has no assigned role');
        setLoading(false);
        return;
      }

      // Get permissions (from cache or fetch)
      console.log(`🔍 [Permission Guard] Fetching permissions for role ${roleId}...`);
      const permissions = await getUserPermissions();
      console.log(`🔍 [Permission Guard] Retrieved ${permissions.length} permissions`);
      console.log('🔍 [Permission Guard] All permissions:', permissions);

      if (!permissions || permissions.length === 0) {
        console.error('❌ [Permission Guard] No permissions found for role');
        setError('No permissions found for your role');
        setLoading(false);
        return;
      }

      // Match object_name case-insensitively (URL often has "lead", DB may have "Lead")
      const objectNameLower = (objectName || '').toLowerCase();
      const permission = permissions.find(
        p => (p.object_name || '').toLowerCase() === objectNameLower
      );
      console.log(`🔍 [Permission Guard] Permission for ${objectName}:`, permission);

      if (!permission || !permission.is_active) {
        console.log(`❌ [Permission Guard] No active permission for ${objectName}`);
        console.log(`   - Permission exists: ${!!permission}`);
        console.log(`   - Is active: ${permission?.is_active}`);
        setError(`You do not have access to ${objectName}`);
        setHasPermission(false);
        setLoading(false);
        return;
      }

      // Check action-specific permission
      const { can_read, can_create, can_update, can_delete, view_all, modify_all } = permission;
      console.log(`🔍 [Permission Guard] Permission flags:`, {
        can_read, can_create, can_update, can_delete, view_all, modify_all
      });

      let allowed = false;

      switch (action) {
        case 'read':
        case 'view':
          allowed = can_read || view_all || modify_all;
          console.log(`🔍 [Permission Guard] Read permission check: ${allowed} (can_read:${can_read}, view_all:${view_all}, modify_all:${modify_all})`);
          break;
        case 'create':
          allowed = can_create || modify_all;
          console.log(`🔍 [Permission Guard] Create permission check: ${allowed} (can_create:${can_create}, modify_all:${modify_all})`);
          break;
        case 'update':
        case 'edit':
          allowed = can_update || modify_all;
          console.log(`🔍 [Permission Guard] Update permission check: ${allowed} (can_update:${can_update}, modify_all:${modify_all})`);
          break;
        case 'delete':
          allowed = can_delete || modify_all;
          console.log(`🔍 [Permission Guard] Delete permission check: ${allowed} (can_delete:${can_delete}, modify_all:${modify_all})`);
          break;
        default:
          allowed = false;
          console.log(`🔍 [Permission Guard] Unknown action: ${action}`);
      }

      if (!allowed) {
        console.log(`❌ [Permission Guard] FINAL RESULT: User cannot ${action} ${objectName}`);
        setError(`You do not have permission to ${action} ${objectName}`);
      } else {
        console.log(`✅ [Permission Guard] FINAL RESULT: User CAN ${action} ${objectName}`);
      }

      setHasPermission(allowed);
      setLoading(false);
      console.log('🔍 [Permission Guard] === PERMISSION CHECK COMPLETE ===');

    } catch (err) {
      console.error('❌ [Permission Guard] Error checking permissions:', err);
      setError('Failed to verify permissions');
      setLoading(false);
    }
  };

  // While checking permission, render children so they can show one combined loader
  // (permission + data). Children read PermissionCheckingContext to keep showing loader.
  if (loading) {
    return (
      <PermissionCheckingContext.Provider value={{ permissionChecking: true }}>
        {children}
      </PermissionCheckingContext.Provider>
    );
  }

  // Permission denied
  if (!hasPermission || error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        padding: '40px'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '600px',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <i className="fas fa-lock" style={{ fontSize: '64px', color: '#dc3545', marginBottom: '20px' }}></i>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Access Denied</h2>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '30px' }}>
            {error || `You do not have permission to ${action} ${objectName}`}
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            Please contact your administrator if you believe you should have access to this page.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => window.history.back()}
            style={{ marginTop: '20px' }}
          >
            <i className="fas fa-arrow-left"></i> Go Back
          </button>
        </div>
      </div>
    );
  }

  // Permission granted - render children (permission checking done)
  return (
    <PermissionCheckingContext.Provider value={{ permissionChecking: false }}>
      {children}
    </PermissionCheckingContext.Provider>
  );
};

export default PermissionProtectedRoute;
